"use client";

import React from "react";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary pour le contenu du dashboard.
 * En prod, si un composant enfant lève une erreur, on affiche ce fallback
 * au lieu d’une zone blanche.
 */
export class DashboardErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Dashboard content error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          className="rounded-xl border border-error-200 bg-error-50/80 p-6 text-error-800"
          role="alert"
        >
          <h3 className="font-semibold text-error-900 mb-1">Erreur d’affichage</h3>
          <p className="text-sm mb-4">
            {this.state.error.message || "Une erreur inattendue s’est produite."}
          </p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            className="rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white hover:bg-error-700"
          >
            Réessayer
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
